import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBusinessPlan } from '../hooks/useBusinessPlan';
import { useTokens } from '../hooks/useTokens';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';
import type { TokenLedgerEntry } from '../types/tokens';
import type { StageProgress } from '../types/businessPlan';
import { STAGE_ORDER, STAGE_CONFIGS } from '../data/stageConfigs';

function daysSince(date: Date): number {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}h ${remMins}m`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export default function BusinessDashboardPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { plan, loading } = useBusinessPlan(planId);
  const { balance, getRecentLedger } = useTokens();
  const [ledgerEntries, setLedgerEntries] = useState<TokenLedgerEntry[]>([]);

  useEffect(() => {
    getRecentLedger(10).then(setLedgerEntries).catch(console.error);
  }, [getRecentLedger]);

  if (loading) return <LoadingScreen />;

  if (!plan) {
    return (
      <AnimatedPage>
        <div className="flex min-h-screen items-center justify-center px-4">
          <h1 className="text-2xl font-bold text-gray-900">Plan not found</h1>
        </div>
      </AnimatedPage>
    );
  }

  const completedCount = plan.completedStages?.length ?? 0;
  const stageProgress = plan.stageProgress ?? {};

  const totalTimeSpent = Object.values(stageProgress).reduce(
    (sum: number, sp: StageProgress) => sum + (sp?.timeSpent ?? 0),
    0,
  );

  const daysSinceStart = plan.createdAt?.toDate
    ? daysSince(plan.createdAt.toDate())
    : 0;

  // Business plan readiness: stages + homework completion
  const homeworkFields = [
    plan.logoKeywords,
    plan.problemDiaryEntries?.length,
    plan.ceoBigPromise,
    plan.friendSurveyResponses?.length,
    plan.wouldYouRatherItems?.length,
    plan.elevatorPitchWho,
  ];
  const homeworkComplete = homeworkFields.filter(Boolean).length;
  const readiness = Math.round(
    ((completedCount + homeworkComplete) / 12) * 100,
  );

  return (
    <AnimatedPage>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white px-4 pb-12 pt-8">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <button
              onClick={() => navigate(`/hq/${planId}`)}
              className="text-sm font-medium text-brand-500 hover:text-brand-600"
            >
              ← Back to HQ
            </button>
            <h1 className="text-xl font-bold text-gray-900 md:text-2xl">
              Business Dashboard
            </h1>
            <div className="w-20" /> {/* spacer */}
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { label: 'Stages Done', value: `${completedCount}/6`, emoji: '🎯' },
              { label: 'Days Active', value: String(daysSinceStart), emoji: '📅' },
              { label: 'Time Spent', value: formatTime(totalTimeSpent), emoji: '⏱️' },
              { label: 'Token Balance', value: String(balance), emoji: '🪙' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border-2 border-gray-100 bg-white p-4 text-center shadow-sm"
              >
                <span className="text-2xl">{stat.emoji}</span>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {stat.value}
                </p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Business Plan Readiness */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mt-6 rounded-2xl border-2 border-gray-100 bg-white p-5 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-gray-900">
                Business Plan Readiness
              </h2>
              <span className="text-lg font-bold text-brand-600">
                {readiness}%
              </span>
            </div>
            <div className="mt-3 h-3 overflow-hidden rounded-full bg-gray-100">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${readiness}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-600"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {completedCount}/6 stages • {homeworkComplete}/6 homework
              activities
            </p>
          </motion.div>

          {/* Progress Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mt-6 rounded-2xl border-2 border-gray-100 bg-white p-5 shadow-sm"
          >
            <h2 className="mb-4 font-bold text-gray-900">
              Progress Timeline
            </h2>
            <div className="space-y-3">
              {STAGE_ORDER.map((key) => {
                const config = STAGE_CONFIGS[key];
                const progress = stageProgress[key];
                const completed = progress?.isCompleted;
                const completedDate = progress?.completedAt?.toDate?.();

                return (
                  <div
                    key={key}
                    className="flex items-center gap-3"
                  >
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm ${
                        completed
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {completed ? '✓' : config.number}
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-sm font-medium ${
                          completed ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        {config.emoji} {config.name}
                      </p>
                    </div>
                    <div className="text-right text-xs text-gray-400">
                      {completed && completedDate
                        ? formatDate(completedDate)
                        : '—'}
                    </div>
                    {progress?.timeSpent ? (
                      <span className="text-xs text-gray-400">
                        {formatTime(progress.timeSpent)}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="mt-6 rounded-2xl border-2 border-gray-100 bg-white p-5 shadow-sm"
          >
            <h2 className="mb-4 font-bold text-gray-900">Recent Activity</h2>
            {ledgerEntries.length === 0 ? (
              <p className="text-sm text-gray-400">
                No activity yet. Complete stages to earn tokens!
              </p>
            ) : (
              <div className="space-y-2">
                {ledgerEntries.map((entry, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {entry.description}
                      </p>
                      <p className="text-xs text-gray-400">
                        {entry.timestamp?.toDate?.()
                          ? formatDate(entry.timestamp.toDate())
                          : ''}
                      </p>
                    </div>
                    <span
                      className={`text-sm font-bold ${
                        entry.amount > 0
                          ? 'text-green-600'
                          : 'text-red-500'
                      }`}
                    >
                      {entry.amount > 0 ? '+' : ''}
                      {entry.amount} 🪙
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </AnimatedPage>
  );
}
